import React, { useState, useEffect } from 'react'
import { X, Calendar, Clock, User, Tag, AlertCircle, Repeat, Bell, Palette } from 'lucide-react'
import { templateAPI } from '../../services/api'

const EventCreationForm = ({ 
  isOpen, 
  onClose, 
  selectedDate = null, 
  onEventCreated,
  templates = [] 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: selectedDate ? new Date(selectedDate).toISOString().slice(0, 16) : '',
    end_date: '',
    event_type: 'design_deadline',
    priority: 'medium',
    status: 'pending',
    template_id: '',
    assigned_to: '',
    reminder_enabled: true,
    reminder_minutes: '15,60',
    is_recurring: false,
    recurring_pattern: '',
    recurring_end_date: '',
    color: '#3B82F6'
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  // Event type options
  const eventTypes = [
    { value: 'design_deadline', label: 'Design Deadline', icon: '🎨' },
    { value: 'review', label: 'Review', icon: '👀' },
    { value: 'meeting', label: 'Meeting', icon: '🤝' },
    { value: 'reminder', label: 'Reminder', icon: '⏰' },
    { value: 'client_feedback', label: 'Client Feedback', icon: '💬' },
    { value: 'approval', label: 'Approval', icon: '✅' },
    { value: 'delivery', label: 'Delivery', icon: '📦' }
  ]

  // Priority options
  const priorities = [
    { value: 'low', label: 'Low', color: 'text-green-600 bg-green-100' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600 bg-yellow-100' },
    { value: 'high', label: 'High', color: 'text-orange-600 bg-orange-100' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600 bg-red-100' }
  ]

  // Recurring patterns
  const recurringPatterns = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ]

  // Color options
  const colorOptions = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ]

  useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        event_date: new Date(selectedDate).toISOString().slice(0, 16)
      }))
    }
  }, [selectedDate])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.title.trim()) {
      errors.title = 'Title is required'
    }

    if (!formData.event_date) {
      errors.event_date = 'Event date is required'
    }

    if (formData.end_date && formData.end_date <= formData.event_date) {
      errors.end_date = 'End date must be after event date'
    }

    if (formData.is_recurring && !formData.recurring_pattern) {
      errors.recurring_pattern = 'Recurring pattern is required for recurring events'
    }

    if (formData.is_recurring && formData.recurring_pattern && !formData.recurring_end_date) {
      errors.recurring_end_date = 'Recurring end date is required for recurring events'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Prepare data for API
      const eventData = {
        ...formData,
        event_date: new Date(formData.event_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        recurring_end_date: formData.recurring_end_date ? new Date(formData.recurring_end_date).toISOString() : null,
        template_id: formData.template_id || null,
        assigned_to: formData.assigned_to || null,
        reminder_minutes: formData.reminder_enabled ? formData.reminder_minutes : null
      }

      const response = await templateAPI.createCalendarEvent(eventData)
      
      if (response.data) {
        onEventCreated?.(response.data)
        onClose()
        // Reset form
        setFormData({
          title: '',
          description: '',
          event_date: '',
          end_date: '',
          event_type: 'design_deadline',
          priority: 'medium',
          status: 'pending',
          template_id: '',
          assigned_to: '',
          reminder_enabled: true,
          reminder_minutes: '15,60',
          is_recurring: false,
          recurring_pattern: '',
          recurring_end_date: '',
          color: '#3B82F6'
        })
      }
    } catch (err) {
      console.error('Error creating event:', err)
      setError(err.response?.data?.detail || 'Failed to create event')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Create New Event</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Basic Information</span>
            </h3>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter event title"
              />
              {validationErrors.title && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter event description"
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>Date & Time</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Event Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.event_date}
                  onChange={(e) => handleInputChange('event_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.event_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.event_date && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.event_date}</p>
                )}
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.end_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.end_date && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.end_date}</p>
                )}
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Tag className="w-5 h-5" />
              <span>Event Details</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type
                </label>
                <select
                  value={formData.event_type}
                  onChange={(e) => handleInputChange('event_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {eventTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {priorities.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Template Selection */}
            {templates.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Associated Template
                </label>
                <select
                  value={formData.template_id}
                  onChange={(e) => handleInputChange('template_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a template (optional)</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Recurring Event */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Repeat className="w-5 h-5" />
              <span>Recurring Event</span>
            </h3>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => handleInputChange('is_recurring', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
                Make this a recurring event
              </label>
            </div>

            {formData.is_recurring && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recurring Pattern *
                  </label>
                  <select
                    value={formData.recurring_pattern}
                    onChange={(e) => handleInputChange('recurring_pattern', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.recurring_pattern ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select pattern</option>
                    {recurringPatterns.map(pattern => (
                      <option key={pattern.value} value={pattern.value}>
                        {pattern.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors.recurring_pattern && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.recurring_pattern}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recurring End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => handleInputChange('recurring_end_date', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.recurring_end_date ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {validationErrors.recurring_end_date && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.recurring_end_date}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Reminders */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Bell className="w-5 h-5" />
              <span>Reminders</span>
            </h3>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="reminder_enabled"
                checked={formData.reminder_enabled}
                onChange={(e) => handleInputChange('reminder_enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="reminder_enabled" className="text-sm font-medium text-gray-700">
                Enable reminders
              </label>
            </div>

            {formData.reminder_enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Times (minutes before event)
                </label>
                <input
                  type="text"
                  value={formData.reminder_minutes}
                  onChange={(e) => handleInputChange('reminder_minutes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 15,60,1440 (15 min, 1 hour, 1 day)"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Enter comma-separated values in minutes (e.g., 15,60,1440)
                </p>
              </div>
            )}
          </div>

          {/* Color */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <Palette className="w-5 h-5" />
              <span>Event Color</span>
            </h3>

            <div className="flex items-center space-x-3">
              {colorOptions.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleInputChange('color', color)}
                  className={`w-8 h-8 rounded-full border-2 ${
                    formData.color === color ? 'border-gray-400' : 'border-gray-200'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Event</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EventCreationForm
